/**
 * ReactionPicker: selector flotante de 6 reacciones tipo Facebook.
 * Aparece on hover/long-press del botón "Reaccionar".
 *
 * Props:
 *   - currentReaction: tipo activo del user (string|null)
 *   - onPick(tipo): callback al elegir/cambiar/quitar
 *   - compact: bool (tamaño chico para comentarios)
 */
import React, { useState, useRef, useEffect } from 'react';

export const REACTIONS = [
  { tipo: 'like',      emoji: '👍', label: 'Me gusta' },
  { tipo: 'love',      emoji: '❤️', label: 'Me encanta' },
  { tipo: 'interesa',  emoji: '💡', label: 'Me interesa' },
  { tipo: 'sorprende', emoji: '😮', label: 'Me sorprende' },
  { tipo: 'triste',    emoji: '😢', label: 'Me entristece' },
  { tipo: 'enoja',     emoji: '😠', label: 'Me enoja' },
];

// Tipos legacy para compat
const LEGACY_MAP = { clap: 'love', idea: 'interesa', check: 'like' };

export const getReactionMeta = (tipo) => {
  const mapped = LEGACY_MAP[tipo] || tipo;
  return REACTIONS.find(r => r.tipo === mapped) || REACTIONS[0];
};

export default function ReactionPicker({ currentReaction, onPick, compact = false, children }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const longPressRef = useRef(null);
  const wrapRef = useRef(null);

  const showSoon = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(true), 400);
  };
  const hideSoon = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(false), 300);
  };

  // Long-press en mobile
  const onTouchStart = (e) => {
    longPressRef.current = setTimeout(() => {
      e.preventDefault();
      setOpen(true);
    }, 350);
  };
  const onTouchEnd = () => clearTimeout(longPressRef.current);

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePick = (tipo) => {
    setOpen(false);
    onPick?.(tipo);
  };

  const current = currentReaction ? getReactionMeta(currentReaction) : null;

  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      onMouseEnter={showSoon}
      onMouseLeave={hideSoon}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Botón disparador: si hay reacción activa la muestra, sino texto */}
      {children ? (
        <span onClick={() => current ? onPick?.(current.tipo) : setOpen(o => !o)}>
          {children}
        </span>
      ) : (
        <button
          type="button"
          onClick={() => current ? onPick?.(current.tipo) : setOpen(o => !o)}
          className={`flex items-center gap-1 ${compact ? 'text-xs' : 'text-sm'} font-medium px-2 py-1 rounded-md transition ${
            current
              ? 'text-violet-600 hover:bg-violet-50'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          aria-label={current ? `Reacción actual: ${current.label}. Click para quitar.` : 'Reaccionar'}
        >
          {current ? <span className="text-base">{current.emoji}</span> : <span>👍</span>}
          <span>{current ? current.label : 'Reaccionar'}</span>
        </button>
      )}

      {/* Picker flotante */}
      {open && (
        <div
          role="menu"
          aria-label="Elegir reacción"
          className="absolute bottom-full left-0 mb-1 bg-white rounded-full shadow-lg border px-1.5 py-1 flex items-center gap-0.5 z-20 animate-in fade-in zoom-in-95 duration-150"
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={hideSoon}
          data-testid="reaction-picker"
        >
          {REACTIONS.map(r => (
            <button
              key={r.tipo}
              type="button"
              onClick={() => handlePick(r.tipo)}
              className="p-1 hover:scale-125 transition-transform"
              aria-label={r.label}
              title={r.label}
              data-testid={`react-${r.tipo}`}
            >
              <span className={`block ${compact ? 'text-xl' : 'text-2xl'}`} aria-hidden="true">{r.emoji}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
